import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { DTHoldingProxy } from '@rabbit/data/types';
import LabelledItemList from '../LabelledItemList/LabelledItemList';
import { ConsumerHoldingSummaryShape } from '@rabbit/bizproc/client';
import CardWrapperWithHeader from '../CardWrapperWithHeader/CardWrapperWithHeader';
import { AdditionalFields, DisplayType } from '@rabbit/helper';
import { ConfigInterface } from '@rabbit/app-context';

export interface AdditionalDetailsViewProps {
  holdingProxy?:
    | DTHoldingProxy
    | ConsumerHoldingSummaryShape
    | null
    | undefined;
  title: string;
  isPartnerToTenant: boolean;
  isCustomerPersona: boolean;
  config: ConfigInterface['config'];
}

export interface FormattedStateProps {
  label: string;
  value: string;
}

function formatKey(key: string) {
  // Create a new key by inserting a space before each uppercase letter and capitalizing each word
  return (
    key
      // Insert a space before each uppercase letter
      .replace(/([A-Z])/g, ' $1')
      // Trim spaces and capitalize the first letter of each word
      .replace(/(?:^|\s)\S/g, (a) => a.toUpperCase())
  );
}

function transformKeys(data: { [key: string]: any }) {
  const newData: { [key: string]: any } = {};
  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const formattedKey = formatKey(key);
      newData[formattedKey] = data[key];
    }
  }
  return newData;
}

const AdditionalDetailsView = ({
  holdingProxy,
  title,
  isPartnerToTenant,
  isCustomerPersona,
  config,
}: AdditionalDetailsViewProps) => {
  const [formatedState, setFormattedState] = useState<FormattedStateProps[]>(
    []
  );

  const filterFieldsByDisplaySettings = (
    fields: { [key: string]: any },
    fieldsConfig: AdditionalFields,
    isPartnerToTenant: boolean,
    isCustomerPersona: boolean
  ): { [key: string]: any } => {
    return Object.entries(fields)
      .filter(([key, _]) => {
        // If no display property or empty display array, allow the field
        const fieldConfig = fieldsConfig[key];
        if (
          !fieldConfig ||
          !fieldConfig.display ||
          fieldConfig.display.length === 0
        ) {
          return true;
        }

        return (
          (isCustomerPersona &&
            fieldConfig.display.includes(
              DisplayType.DISPLAY_FOR_CONSUMER // Display for consumer if the user is a customer persona
            )) ||
          fieldConfig.display.includes(
            isPartnerToTenant
              ? DisplayType.DISPLAY_FOR_PARTNERS // Display for partners if the user is a partner to the tenant
              : DisplayType.DISPLAY_FOR_TENANT // Otherwise, display for the tenant
          )
        );
      })
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as { [key: string]: any });
  };

  useEffect(() => {
    if (holdingProxy) {
      const transformedAdditionalData = transformKeys(
        filterFieldsByDisplaySettings(
          holdingProxy.additionalData,
          config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS,
          isPartnerToTenant,
          isCustomerPersona
        ) || {}
      );

      const formattedData = Object.entries(transformedAdditionalData).map(
        ([label, value]) => ({
          label,
          value:
            typeof value === 'boolean'
              ? value
                ? 'Yes'
                : 'No'
              : value?.toString() || '-',
        })
      );

      setFormattedState(formattedData);
    }
  }, [holdingProxy]);

  return (
    <CardWrapperWithHeader title={title}>
      {!holdingProxy && <RenderLoadingState />}
      {formatedState.length > 0 && <LabelledItemList items={formatedState} />}
    </CardWrapperWithHeader>
  );
};

const RenderLoadingState = () => (
  <div className="flex flex-col gap-6 p-5">
    <div className="grid grid-cols-2 gap-5 md:grid-cols-6">
      <div className="w-full">
        {' '}
        <Skeleton count={2} baseColor="#fff" highlightColor="#f1f1f1" />
      </div>
      <div className="w-full">
        {' '}
        <Skeleton count={2} baseColor="#fff" highlightColor="#f1f1f1" />
      </div>
      <div className="w-full">
        {' '}
        <Skeleton count={2} baseColor="#fff" highlightColor="#f1f1f1" />
      </div>
      <div className="w-full">
        {' '}
        <Skeleton count={2} baseColor="#fff" highlightColor="#f1f1f1" />
      </div>
      <div className="w-full">
        {' '}
        <Skeleton count={2} baseColor="#fff" highlightColor="#f1f1f1" />
      </div>
      <div className="w-full">
        {' '}
        <Skeleton count={2} baseColor="#fff" highlightColor="#f1f1f1" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-5 md:grid-cols-6">
      <div className="w-full">
        {' '}
        <Skeleton count={2} baseColor="#fff" highlightColor="#f1f1f1" />
      </div>
      <div className="w-full">
        {' '}
        <Skeleton count={2} baseColor="#fff" highlightColor="#f1f1f1" />
      </div>
    </div>
  </div>
);

export default AdditionalDetailsView;
